import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

// ── Transaction Drawer ─────────────────────────────────────────────────────────
function TxDrawer({ mailbox, onClose }) {
  const api = useApi();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date, mailboxid: mailbox.mailboxid, limit: 500 });
      const [txData, sumData] = await Promise.all([
        api.get(`/dashboard/tr-logs?${params}`),
        api.get(`/dashboard/tr-summary?${params}`),
      ]);
      setRows(txData.rows || []);
      setSummary(sumData);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api, date, mailbox.mailboxid]);

  useEffect(() => { load(); }, [load]);

  const getStatusBadge = (r) => {
    const ok = (r.cpi_status === 0 || r.cpi_status === '0') &&
               (r.gateway_status === 0 || r.gateway_status === '0');
    return ok
      ? <span className="badge badge-success">OK</span>
      : <span className="badge badge-error">{r.cpi_status !== 0 ? `CPI: ${r.cpi_status}` : `GW: ${r.gateway_status}`}</span>;
  };

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.5)',
      }} />

      {/* Drawer */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: '80%', maxWidth: 900,
        background: 'var(--bg-card)',
        borderLeft: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.25s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{mailbox.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {mailbox.mailboxid} {mailbox.industryno ? `· BHF ${mailbox.industryno}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="date" className="form-input"
              value={date} max={today}
              onChange={e => setDate(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px' }}
            />
            <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Summary strip */}
        {summary && (
          <div style={{
            display: 'flex', gap: 0,
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}>
            {[
              { label: 'Total',      value: summary.total,      color: 'var(--accent)'  },
              { label: 'Successful', value: summary.successful,  color: 'var(--green)'   },
              { label: 'Failed',     value: summary.failed,      color: 'var(--red)'     },
              { label: 'Avg Time',   value: `${summary.avgTime} ms`, color: 'var(--yellow)' },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, padding: '10px 16px',
                borderRight: '1px solid var(--border)',
                display: 'flex', flexDirection: 'column', gap: 2,
              }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, color: s.color }}>
                  {typeof s.value === 'number' ? s.value.toLocaleString() : s.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Destination</th>
                <th>Type</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>CPI→GW</th>
                <th style={{ textAlign: 'right' }}>Remote</th>
                <th style={{ textAlign: 'right', color: 'var(--yellow)' }}>Total</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>{[...Array(8)].map((_, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 12, width: '70%' }} /></td>
                  ))}</tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>
                  No transactions found for this date
                </td></tr>
              ) : rows.map((r, i) => {
                const totalTime = r.totaltime ?? ((r.uptime || 0) + (r.remotetime || 0) + (r.downtime || 0));
                const cpiGwTime = (r.uptime || 0) + (r.downtime || 0);
                return (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {new Date(r.date).toLocaleString()}
                    </td>
                    <td>{r.destination_mailbox || '—'}</td>
                    <td>{r.message_type || '—'}</td>
                    <td>{getStatusBadge(r)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{cpiGwTime} ms</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.remotetime || 0} ms</td>
                    <td style={{
                      textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
                      color: totalTime > 5000 ? 'var(--red)' : totalTime > 2000 ? 'var(--yellow)' : 'var(--green)',
                    }}>{totalTime} ms</td>
                    <td style={{ color: 'var(--red)', fontSize: 11, maxWidth: 180 }}>
                      {r.error_message
                        ? <span title={r.error_message}>{String(r.error_message).slice(0, 30)}{String(r.error_message).length > 30 ? '…' : ''}</span>
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!loading && rows.length > 0 && (
          <div style={{
            padding: '8px 16px', borderTop: '1px solid var(--border)',
            fontSize: 11, color: 'var(--text-muted)', flexShrink: 0,
          }}>
            Showing {rows.length} transaction{rows.length !== 1 ? 's' : ''}
            {rows.length === 500 ? ' (limit reached — use Reports for full history)' : ''}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function MailboxModal({ mailbox, branches, onClose, onSaved }) {
  const api = useApi();
  const isEdit = !!mailbox?.mailboxid;
  const [form, setForm] = useState(mailbox || {
    mailboxid: '', name: '', industryno: '', email: '', telno: '', faxno: '',
    manager: '', contact: '', mobileno: '', software: '', version: '',
    saddress: '', paddress: '', city: '', notes: '', sendlogs: 0, sendstats: 0,
    blocked: 'N', deleted: 'N', branchkey: '-1',
    escripting: 0, latitude: '', longitude: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/mailboxes/${form.mailboxid}`, form);
        setMsg('Mailbox updated successfully');
      } else {
        await api.post('/mailboxes', form);
        setMsg('Mailbox created successfully');
      }
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const SEND_OPTIONS = ['Do not send', 'Send Daily', 'Send Weekly', 'Send Monthly'];

  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? `Edit Mailbox — ${form.mailboxid}` : 'Add Mailbox'}</div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {msg && <div className="alert alert-success">{msg}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Mailbox ID</label>
            <input className="form-input" value={form.mailboxid} onChange={set('mailboxid')} disabled={isEdit} />
          </div>
          <div className="form-group">
            <label className="form-label">Industry No</label>
            <input className="form-input" value={form.industryno || ''} onChange={set('industryno')} />
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name || ''} onChange={set('name')} />
          </div>
          <div className="form-group">
            <label className="form-label">Manager</label>
            <input className="form-input" value={form.manager || ''} onChange={set('manager')} />
          </div>
          <div className="form-group">
            <label className="form-label">Contact</label>
            <input className="form-input" value={form.contact || ''} onChange={set('contact')} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email || ''} onChange={set('email')} />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile No</label>
            <input className="form-input" value={form.mobileno || ''} onChange={set('mobileno')} />
          </div>
          <div className="form-group">
            <label className="form-label">Tel No</label>
            <input className="form-input" value={form.telno || ''} onChange={set('telno')} />
          </div>
          <div className="form-group">
            <label className="form-label">Fax No</label>
            <input className="form-input" value={form.faxno || ''} onChange={set('faxno')} />
          </div>
          <div className="form-group">
            <label className="form-label">Software</label>
            <input className="form-input" value={form.software || ''} onChange={set('software')} />
          </div>
          <div className="form-group">
            <label className="form-label">Version</label>
            <input className="form-input" value={form.version || ''} onChange={set('version')} />
          </div>
          <div className="form-group">
            <label className="form-label">Postal Address</label>
            <textarea className="form-textarea" rows={3} value={form.paddress || ''} onChange={set('paddress')} />
          </div>
          <div className="form-group">
            <label className="form-label">Physical Address</label>
            <textarea className="form-textarea" rows={3} value={form.saddress || ''} onChange={set('saddress')} />
          </div>
          <div className="form-group">
            <label className="form-label">City / Town</label>
            <input className="form-input" value={form.city || ''} onChange={set('city')} placeholder="e.g. Johannesburg" />
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes || ''} onChange={set('notes')} />
          </div>
          <div className="form-group">
            <label className="form-label">Support Branch</label>
            <select className="form-select" value={form.branchkey || '-1'} onChange={set('branchkey')}>
              <option value="-1">— Select Branch —</option>
              {branches?.map(b => <option key={b.branchkey} value={b.branchkey}>{b.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Account Blocked</label>
            <select className="form-select" value={form.blocked || 'N'} onChange={set('blocked')}>
              <option value="N">No</option>
              <option value="Y">Yes</option>
              {isEdit && <option value="D">Delete</option>}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Email Tx Logs</label>
            <select className="form-select" value={form.sendlogs || 0} onChange={set('sendlogs')}>
              {SEND_OPTIONS.map((o, i) => <option key={i} value={i}>{o}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Email Tx Statistics</label>
            <select className="form-select" value={form.sendstats || 0} onChange={set('sendstats')}>
              {SEND_OPTIONS.map((o, i) => <option key={i} value={i}>{o}</option>)}
            </select>
          </div>

          {/* eScripting + Coordinates section */}
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <div style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 14, marginTop: 4,
              fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12,
            }}>
              eScripting &amp; Location
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">eScripting Enabled</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 6 }}>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, escripting: f.escripting ? 0 : 1 }))}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: form.escripting ? 'var(--green)' : 'var(--border)',
                      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3, left: form.escripting ? 22 : 3,
                      width: 18, height: 18, borderRadius: '50%', background: 'white',
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                  <span style={{ fontSize: 13, color: form.escripting ? 'var(--green)' : 'var(--text-muted)', fontWeight: 600 }}>
                    {form.escripting ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Latitude</label>
                <input className="form-input" type="number" step="0.0000001"
                  placeholder="e.g. -25.7460"
                  value={form.latitude ?? ''}
                  onChange={e => setForm(f => ({ ...f, latitude: e.target.value === '' ? null : e.target.value }))}
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Longitude</label>
                <input className="form-input" type="number" step="0.0000001"
                  placeholder="e.g. 28.1879"
                  value={form.longitude ?? ''}
                  onChange={e => setForm(f => ({ ...f, longitude: e.target.value === '' ? null : e.target.value }))}
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving…' : isEdit ? 'Update Mailbox' : 'Create Mailbox'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Mailboxes() {
  const api = useApi();
  const { user } = useAuth();
  const [mailboxes, setMailboxes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [txDrawer, setTxDrawer] = useState(null); // mailbox object | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mb, br] = await Promise.all([
        api.get(`/mailboxes${search ? `?search=${encodeURIComponent(search)}` : ''}`),
        api.get('/branches'),
      ]);
      setMailboxes(mb);
      setBranches(br);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  const canEdit = user?.mailboxaccesslevel >= 2;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mailboxes</h1>
          <div className="page-subtitle">{mailboxes.length} accounts</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="search-bar">
            <span style={{ color: 'var(--text-muted)' }}>⌕</span>
            <input
              placeholder="Search mailboxes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setModal('add')}>+ Add Mailbox</button>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Mailbox ID</th>
                <th>Name</th>
                <th>Industry No</th>
                <th>Branch</th>
                <th>Software</th>
                <th>eScripting</th>
                <th>Status</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>
                    ))}
                  </tr>
                ))
              ) : mailboxes.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>
                  {search ? 'No mailboxes match your search' : 'No mailboxes found'}
                </td></tr>
              ) : mailboxes.map(mb => (
                <tr key={mb.mailboxid}>
                  <td style={{ color: 'var(--accent)', fontWeight: 600 }}>{mb.mailboxid}</td>
                  <td style={{ color: 'var(--text-primary)' }}>{mb.name}</td>
                  <td>{mb.industryno || '—'}</td>
                  <td>{mb.branchname || '—'}</td>
                  <td>{mb.software || '—'}</td>
                  <td>
                    {mb.escripting
                      ? <span className="badge badge-success">eRx</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                  <td>
                    {mb.blocked === 'Y'
                      ? <span className="badge badge-error">Blocked</span>
                      : mb.deleted === 'Y'
                      ? <span className="badge badge-warning">Deleted</span>
                      : <span className="badge badge-success">Active</span>}
                  </td>
                  <td>{mb.email || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setTxDrawer(mb)}>⇄ Transactions</button>
                      {canEdit && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setModal(mb)}>Edit</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <MailboxModal
          mailbox={modal === 'add' ? null : modal}
          branches={branches}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}

      {txDrawer && (
        <TxDrawer mailbox={txDrawer} onClose={() => setTxDrawer(null)} />
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}
